import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PremiumLock } from "@/components/PremiumLock";
import { useMemo, useState } from "react";
import { usd } from "@/lib/format";
import { AlertTriangle, CheckCircle2, Calculator, Sparkles, TrendingDown, Save } from "lucide-react";
import { lenders } from "@/lib/mock-data";
import { saveAnalysis } from "@/utils/analyses.functions";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";

export const Route = createFileRoute("/_authenticated/analyzer")({
  head: () => ({ meta: [{ title: "Loan Analyzer — LendyTrack" }] }),
  component: Analyzer,
});

function monthlyPayment(P: number, aprPct: number, months: number) {
  const r = aprPct / 100 / 12;
  if (r === 0) return P / months;
  return (P * r) / (1 - Math.pow(1 + r, -months));
}

function Analyzer() {
  const [amount, setAmount] = useState(2000);
  const [apr, setApr] = useState(89);
  const [fees, setFees] = useState(150);
  const [term, setTerm] = useState(12);

  const m = useMemo(() => monthlyPayment(amount + fees, apr, term), [amount, apr, fees, term]);
  const total = m * term;
  const interest = total - amount - fees;

  const flags: { tone: "danger" | "warning" | "success"; text: string }[] = [];
  if (apr >= 36) flags.push({ tone: "danger", text: `APR of ${apr}% exceeds the 36% safe-borrowing benchmark by ${(apr / 36).toFixed(1)}x.` });
  else if (apr >= 25) flags.push({ tone: "warning", text: `APR of ${apr}% is above prime range. Check pre-qualified offers elsewhere first.` });
  else flags.push({ tone: "success", text: `APR of ${apr}% is within mainstream personal-loan range.` });

  const feeRatio = fees / amount;
  if (feeRatio > 0.05) flags.push({ tone: "warning", text: `Origination/fees are ${(feeRatio * 100).toFixed(1)}% of principal. Industry typical is under 5%.` });
  if (interest > amount) flags.push({ tone: "danger", text: `You'll pay more in interest (${usd(interest)}) than you borrowed (${usd(amount)}).` });
  if (term <= 6 && amount >= 1000) flags.push({ tone: "warning", text: `Short term creates a high monthly payment — affordability risk.` });

  const recommendation = flags.some((f) => f.tone === "danger") ? "Avoid this loan" : flags.some((f) => f.tone === "warning") ? "Compare carefully" : "Reasonable terms";
  const { isPremium } = useSubscription();
  const [saving, setSaving] = useState(false);

  const analysisPayload = useMemo(
    () => ({
      amount,
      apr,
      fees,
      term,
      monthly: m,
      total,
      interest,
      verdict: recommendation,
      flags,
    }),
    [amount, apr, fees, term, m, total, interest, recommendation, flags],
  );

  const handleSave = async () => {
    if (!isPremium) return;
    setSaving(true);
    try {
      await saveAnalysis({
        data: {
          title: `Loan analysis — ${usd(amount)} at ${apr}% APR`,
          payload: analysisPayload,
        },
      });
      toast.success("Analysis saved");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to save analysis");
    } finally {
      setSaving(false);
    }
  };

  const alternatives = lenders
    .filter((l) => l.recommendation === "safe" && amount >= l.amountRange[0] && amount <= l.amountRange[1])
    .slice(0, 3);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Loan analyzer</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Is this loan a good deal?</h1>
        <p className="mt-1 text-sm text-muted-foreground">Enter your offer. We'll surface the true cost, hidden risks, and safer alternatives.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
            <Calculator className="h-3 w-3" /> Loan offer
          </div>
          <div className="mt-4 space-y-5">
            {[
              { label: "Loan amount", v: amount, setter: setAmount, min: 100, max: 50000, step: 100, prefix: "$" },
              { label: "APR (%)", v: apr, setter: setApr, min: 0, max: 700, step: 0.5, prefix: "" },
              { label: "Fees", v: fees, setter: setFees, min: 0, max: 5000, step: 25, prefix: "$" },
              { label: "Term (months)", v: term, setter: setTerm, min: 1, max: 84, step: 1, prefix: "" },
            ].map((f) => (
              <div key={f.label}>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{f.label}</label>
                  <div className="text-sm font-semibold tabular-nums">{f.prefix}{typeof f.v === "number" ? f.v.toLocaleString() : f.v}</div>
                </div>
                <input
                  type="range"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={f.v}
                  onChange={(e) => f.setter(parseFloat(e.target.value))}
                  className="mt-2 w-full accent-[oklch(0.68_0.16_160)]"
                />
                <input
                  type="number"
                  value={f.v}
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  onChange={(e) => f.setter(parseFloat(e.target.value) || 0)}
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className={`rounded-2xl border p-6 shadow-soft ${
            recommendation === "Avoid this loan" ? "border-danger/40 bg-danger/5" :
            recommendation === "Compare carefully" ? "border-warning/50 bg-warning/10" :
            "border-success/40 bg-success/5"
          }`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Verdict</div>
            <div className={`mt-1 font-display text-3xl font-semibold ${
              recommendation === "Avoid this loan" ? "text-danger" :
              recommendation === "Compare carefully" ? "text-warning-foreground" :
              "text-success"
            }`}>{recommendation}</div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Monthly</div>
              <div className="mt-1 font-display text-2xl font-semibold">{usd(m)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Total repayment</div>
              <div className="mt-1 font-display text-2xl font-semibold">{usd(total)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Total interest</div>
              <div className="mt-1 font-display text-2xl font-semibold text-danger">{usd(interest)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Cost per $1 borrowed</div>
              <div className="mt-1 font-display text-2xl font-semibold">${(total / amount).toFixed(2)}</div>
            </div>
          </div>

          <PremiumLock
            title="Full AI analysis"
            description="Premium unlocks the line-by-line risk breakdown, hidden-fee detection, and personalized recommendations."
          >
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
                <Sparkles className="h-3 w-3" /> AI analysis
              </div>
              <div className="mt-4 space-y-3">
                {flags.map((f, i) => (
                  <div key={i} className={`flex gap-3 rounded-lg border p-3 ${
                    f.tone === "danger" ? "border-danger/30 bg-danger/5" :
                    f.tone === "warning" ? "border-warning/40 bg-warning/10" :
                    "border-success/30 bg-success/5"
                  }`}>
                    {f.tone === "danger" ? <AlertTriangle className="h-4 w-4 shrink-0 text-danger" /> :
                     f.tone === "warning" ? <AlertTriangle className="h-4 w-4 shrink-0 text-warning-foreground" /> :
                     <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />}
                    <div className="text-sm">{f.text}</div>
                  </div>
                ))}
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save analysis"}
              </button>
            </div>
          </PremiumLock>


          {alternatives.length > 0 && (
            <div className="rounded-2xl border border-success/30 bg-success/5 p-6">
              <div className="flex items-center gap-2 text-success">
                <TrendingDown className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-widest">Safer alternatives at this amount</span>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {alternatives.map((a) => {
                  const altApr = (a.aprRange[0] + a.aprRange[1]) / 2;
                  const altM = monthlyPayment(amount, altApr, term);
                  const altTotal = altM * term;
                  return (
                    <div key={a.slug} className="rounded-xl border border-border bg-card p-4">
                      <div className="font-display text-base font-semibold">{a.name}</div>
                      <div className="text-xs text-muted-foreground">~{altApr.toFixed(1)}% APR</div>
                      <div className="mt-3 text-sm">Save ~<span className="font-semibold text-success">{usd(Math.max(0, total - altTotal))}</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
